import { useState, useMemo } from 'react'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import { Search } from 'lucide-react'
import { debounce } from 'lodash'

type ServerAutocompleteMultipleProps<T> = {
  values: T[]
  onChange: (values: T[]) => void
  fetchService: (search: string) => Promise<T[]>
  getOptionLabel: (option: T) => string
  placeholder?: string
  label?: string
  minSearchLength?: number
  debounceMs?: number
  error?: boolean
  helperText?: string
}

export default function ServerAutocompleteMultiple<T>({
  values,
  onChange,
  fetchService,
  getOptionLabel,
  placeholder = 'Rechercher...',
  label = 'Rechercher',
  minSearchLength = 2,
  debounceMs = 300,
  error = false,
  helperText = '',
}: ServerAutocompleteMultipleProps<T>) {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<T[]>([])
  const [loading, setLoading] = useState(false)

  const handleSearch = useMemo(
    () =>
      debounce(async (term: string) => {
        if (term.length < minSearchLength) {
          setOptions([])
          setLoading(false)
          return
        }
        setLoading(true)
        try {
          const results = await fetchService(term)
          const selectedIds = new Set(values.map((v) => (v as any).id))
          const filtered = results.filter((r) => !selectedIds.has((r as any).id))
          setOptions(filtered)
        } catch {
          setOptions([])
        } finally {
          setLoading(false)
        }
      }, debounceMs),
    [fetchService]
  )

  return (
    <Autocomplete<T, true, false, false>
      multiple
      value={values}
      onChange={(_, newValue, reason) => {
        onChange(newValue as T[])
        if (reason === 'selectOption') {
          setInputValue('')
          setOptions([])
          fetchService('')
        }
      }}
      options={options}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'reset') return
        setInputValue(newInputValue)
        handleSearch(newInputValue)
      }}
      getOptionLabel={getOptionLabel}
      filterOptions={(x) => x}
      loading={loading}
      noOptionsText="Aucun résultat"
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <Search size={20} className="text-gray-500" />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}
